import React, { useEffect, useState, useRef } from 'react';
import { useNav } from '../../hooks/nav';
import './style.scss';
import { t } from '@web-solutions/module-localization';
import { Title } from 'src/onboarding/components/typography/Title/Title';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import { colors } from 'src/styles/colors';
import { ModalAnalysis } from 'src/onboarding/components/ui/modalAnalysis/ModalAnalysis';
import { Check } from 'src/onboarding/components/ui/check/Check';
import { Option } from 'src/onboarding/components/ui/check/Option';
import { ReactComponent as ActiveSVG } from '../../assets/images/CheckWhite.svg';
import { ReactComponent as StarSvg } from '../../assets/images/star.svg';
import tashaImg from '../../assets/images/tasha.png';

import sophiaImg from '../../assets/images/sophia.png';

import isabellaImg from '../../assets/images/isabella.png';
import JamesImg from '../../assets/images/james.webp';
import DanielImg from '../../assets/images/daniel.webp';
import MichaelImg from '../../assets/images/michael.webp';
import { useStore } from 'src/onboarding/store/store';
import Analytics from '@web-solutions/module-analytics';

const Page = () => {
  const [percentage, setPercentage] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [questionIndex, setQuestionIndex] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [activeBar, setActiveBar] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const startRef = useRef(performance.now());
  const animationRef = useRef({
    hasCompleted: false,
    animationId: null,
  });

  const gender = useStore((state) => state.gender);
  const isMale = gender === 'Male';

  const { onNext, currentIndex } = useNav();

  const questions = [
    {
      title: t('onboarding.analysisResults.modal.1.title'),
      options: [
        t('onboarding.analysisResults.modal.1.option1'),
        t('onboarding.analysisResults.modal.1.option2'),
      ],
      showAt: 20,
    },
    {
      title: t('onboarding.analysisResults.modal.2.title'),
      options: [
        t('onboarding.analysisResults.modal.2.option1'),
        t('onboarding.analysisResults.modal.2.option2'),
      ],
      showAt: 40,
    },
    {
      title: t('onboarding.analysisResults.modal.3.title'),
      options: [
        t('onboarding.analysisResults.modal.3.option1'),
        t('onboarding.analysisResults.modal.3.option2'),
      ],
      showAt: 60,
    },
  ];

  const analysis = [
    { id: 1, label: t('onboarding.analysisResults.options.answers'), value: 1 },
    { id: 2, label: t('onboarding.analysisResults.options.trends'), value: 1 },
    { id: 3, label: t('onboarding.analysisResults.options.goal'), value: 1 },
    { id: 4, label: t('onboarding.analysisResults.options.plan'), value: 1 },
  ];

  const customers = [
    {
      img: tashaImg,
      name: t('onboarding.personalPlan.customers.1.name'),
      descriptions: t('onboarding.personalPlan.customers.1.descriptions'),
    },
    {
      img: sophiaImg,
      name: t('onboarding.personalPlan.customers.2.name'),
      descriptions: t('onboarding.personalPlan.customers.2.descriptions'),
    },
    {
      img: isabellaImg,
      name: t('onboarding.personalPlan.customers.3.name'),
      descriptions: t('onboarding.personalPlan.customers.3.descriptions'),
    },
  ];

  const customersMale = [
    {
      img: JamesImg,
      name: t('onboarding.analysisResults.customers.1.name'),
      descriptions: t('onboarding.analysisResults.customers.1.description'),
    },
    {
      img: DanielImg,
      name: t('onboarding.analysisResults.customers.2.name'),
      descriptions: t('onboarding.analysisResults.customers.2.description'),
    },
    {
      img: MichaelImg,
      name: t('onboarding.analysisResults.customers.3.name'),
      descriptions: t('onboarding.analysisResults.customers.3.description'),
    },
  ];

  useEffect(() => {
    const duration = 10000;

    const animate = (time) => {
      if (isPaused) {
        setElapsedTime(time - startRef.current);
        return;
      }

      const progress = Math.min((time - startRef.current) / duration, 1);
      const newPercentage = Math.round(progress * 100);
      setPercentage(newPercentage);

      const expectedActive = Math.floor(newPercentage / 25);

      if (expectedActive !== activeBar && expectedActive < analysis.length) {
        setActiveBar(expectedActive);
      }

      const upcomingQuestion = questions.find(
        (q, i) => q.showAt === newPercentage && i !== questionIndex
      );
      if (upcomingQuestion) {
        setIsPaused(true);
        setIsModalOpen(true);
        setQuestionIndex(questions.indexOf(upcomingQuestion));
        return;
      }

      if (newPercentage < 25) {
        setActiveBar(0);
      } else if (newPercentage >= 25 && newPercentage < 50) {
        setActiveBar(1);
      } else if (newPercentage >= 50 && newPercentage < 75) {
        setActiveBar(2);
      } else if (newPercentage >= 75) {
        setActiveBar(3);
      }

      if (newPercentage >= 100 && !animationRef.current.hasCompleted) {
        animationRef.current.hasCompleted = true;
        cancelAnimationFrame(animationRef.current.animationId);
        Analytics.trackEvent(`step${currentIndex + 1}`, 'complete', {
          page: 'analyzing-answers',
        });
        onNext();
        return;
      }

      if (progress < 1) {
        animationRef.current.animationId = requestAnimationFrame(animate);
      }
    };

    animationRef.current.hasCompleted = false;
    animationRef.current.animationId = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current.animationId) {
        cancelAnimationFrame(animationRef.current.animationId);
      }
    };
  }, [isPaused, activeBar]);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isModalOpen]);

  const handleModalAnswer = () => {
    if (isProcessing) return;

    setIsProcessing(true);
    startRef.current = performance.now() - elapsedTime;
    setIsModalOpen(false);
    setIsPaused(false);

    setTimeout(() => setIsProcessing(false), 1000);
  };

  return (
    <div className={'layout analyzing-page'}>
      <div style={{ marginTop: '40px', marginBottom: '8px' }}>
        <div style={{ position: 'relative', width: 145, height: 145, margin: '0 auto' }}>
          <CircularProgressbar
            value={percentage}
            styles={buildStyles({
              pathColor: 'rgb(0, 206, 0)',
              trailColor: colors.baseWhite,
              strokeLinecap: 'round',
            })}
            text={''}
          />
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: 40,
              lineHeight: '40px',
              letterSpacing: '-0.032px',
              fontWeight: 700,
              fontFamily: 'Inter',
            }}
          >
            {percentage}%
          </div>
        </div>
      </div>

      <Title tag='h2'>{t('onboarding.analysisResults.title')}</Title>
      <div className='content' style={{ marginTop: '40px' }}>
        <div className='analysis'>
          {analysis.map((option, index) => (
            <Progress
              key={option.id}
              value={option.value}
              label={option.label}
              percentage={percentage}
              index={index}
              activeBar={activeBar}
            />
          ))}
        </div>
        {isModalOpen && questionIndex !== null && (
          <ModalAnalysis isOpen={isModalOpen} title={questions[questionIndex].title}>
            <Check onChange={handleModalAnswer}>
              {questions[questionIndex].options.map((text, i) => (
                <>
                  <Option value={text} key={i}>
                    {text}
                  </Option>
                </>
              ))}
            </Check>
          </ModalAnalysis>
        )}
        <div className={'feedbacks'}>
          {(isMale ? customersMale : customers).map(
            ({ img, name, descriptions, weightLeft, weightRight }) => (
              <div className='feedback' key={name}>
                <div className={'feedbackImgContainer'}>
                  <img
                    className={isMale ? 'feedbackImgMale' : 'feedbackImg'}
                    src={img}
                    alt='customer'
                  />
                </div>

                <div className={'feedbackTop'}>
                  <div className={'feedbackContainer'}>
                    <span className={'feedbackName'}>{name}</span>
                    <span className={'feedbackRating'}>
                      {[...Array(5).keys()].map((_, i) => (
                        <StarSvg key={i} />
                      ))}
                    </span>
                  </div>
                  <div className={'feedbackBody'}>{descriptions}</div>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default Page;

const Progress = ({ label, percentage, index, activeBar }) => {

  const position = index - activeBar;

  let blurClass = '';
  if (position === 1) blurClass = 'blur-4';
  else if (position > 1) blurClass = 'blur-8';

  const isHidden = position < 0;

  const segmentStart = index * 25;
  const segmentEnd = segmentStart + 25;

  let localPercentage = 0;
  if (percentage >= segmentEnd) {
    localPercentage = 100;
  } else if (percentage >= segmentStart) {
    localPercentage = ((percentage - segmentStart) / 25) * 100;
  }

  const isActive = percentage >= segmentStart && percentage < segmentEnd;
  const isCompleted = percentage >= segmentEnd;

  const shiftClass = isCompleted ? 'shifted' : '';

  return (
    <div
      className={`progress 
             ${isCompleted ? 'completed' : ''} 
             ${isActive ? 'active' : ''} 
             ${blurClass} 
             ${isHidden ? 'hide' : ''} 
             ${shiftClass}`}
    >
      <div className={'progress-bar'}>
        <div className='progress-fill' style={{ width: `${localPercentage}%` }}></div>
        <div className='header'>
          <span>{label}</span>
          {isCompleted && <ActiveSVG />}
        </div>
      </div>
    </div>
  );
};
